import { ElementType, ReactElement } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useSetAtom } from "jotai";

import { handleOpenDlgContactUs } from "@pages/contact/ContactUsDialog";

type ProductItem = {
  title: string;
  description: string;
  icon: ElementType;
};

type FlowItem = {
  label: string;
  detail: string;
  icon: ElementType;
};

type ValueItem = {
  title: string;
  description: string;
};

type ProductDetailPageProps = {
  eyebrow: string;
  title: string;
  description: string;
  summaryTitle: string;
  summaryDescription: string;
  flowTitle: string;
  flow: FlowItem[];
  capabilities: ProductItem[];
  valuesTitle: string;
  valuesDescription: string;
  values: ValueItem[];
  contactTitle: string;
  colors: {
    dark: string;
    darkAlt: string;
    primary: string;
    pale: string;
    accent: string;
    text: string;
  };
};

const ProductDetailPage = ({
  eyebrow,
  title,
  description,
  summaryTitle,
  summaryDescription,
  flowTitle,
  flow,
  capabilities,
  valuesTitle,
  valuesDescription,
  values,
  contactTitle,
  colors,
}: ProductDetailPageProps): ReactElement => {
  const openContactUsDlg = useSetAtom(handleOpenDlgContactUs);
  const openContact = () => openContactUsDlg(() => undefined);

  return (
    <Box component="main" sx={{ bgcolor: "#f8faf9", color: colors.text }}>
      <Box
        component="section"
        sx={{
          overflow: "hidden",
          pt: { xs: 15, md: 18 },
          pb: { xs: 8, md: 11 },
          color: "#fff",
          backgroundImage: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkAlt} 100%), repeating-linear-gradient(90deg, transparent 0, transparent 79px, rgba(255,255,255,0.06) 80px)`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid xs={12} md={7}>
              <Typography component="p" sx={{ color: `${colors.primary} !important`, fontSize: 14, fontWeight: 700, mb: 2 }}>
                {eyebrow}
              </Typography>
              <Typography component="h1" sx={{ fontSize: { xs: 38, sm: 48, md: 56 }, fontWeight: 800, lineHeight: 1.25, mb: 3 }}>
                {title}
              </Typography>
              <Typography sx={{ maxWidth: 680, color: "#eef4f2 !important", fontSize: { xs: 16, md: 18 }, lineHeight: 1.9 }}>
                {description}
              </Typography>
              <Button
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={openContact}
                sx={{ mt: 4, px: 3, py: 1.4, bgcolor: colors.accent, color: colors.text, fontWeight: 700, boxShadow: "none", "&:hover": { bgcolor: colors.primary, boxShadow: "none" } }}
              >
                導入について相談する
              </Button>
            </Grid>
            <Grid xs={12} md={5}>
              <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2, bgcolor: "rgba(255,255,255,0.09)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", backdropFilter: "blur(8px)" }}>
                <Typography sx={{ color: "#fff !important", fontSize: 18, fontWeight: 700, mb: 3 }}>{flowTitle}</Typography>
                <Stack spacing={1.5}>
                  {flow.map((step, index) => {
                    const StepIcon = step.icon;
                    return (
                      <Stack key={step.label} direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ width: 44, height: 44, flexShrink: 0, display: "grid", placeItems: "center", borderRadius: "50%", bgcolor: index === flow.length - 1 ? colors.accent : "rgba(255,255,255,0.16)", color: index === flow.length - 1 ? colors.text : "#fff" }}>
                          <StepIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: "#fff !important", fontWeight: 700 }}>{step.label}</Typography>
                          <Typography sx={{ color: "#cedbd7 !important", fontSize: 13 }}>{step.detail}</Typography>
                        </Box>
                        {index < flow.length - 1 && <ArrowForwardRoundedIcon sx={{ ml: "auto !important", color: colors.primary }} />}
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container component="section" maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
        <Box sx={{ maxWidth: 740, mb: 6 }}>
          <Chip label="CORE FUNCTIONS" size="small" sx={{ mb: 2, bgcolor: colors.pale, color: colors.dark, fontWeight: 700 }} />
          <Typography component="h2" sx={{ fontSize: { xs: 30, md: 40 }, fontWeight: 800, mb: 2 }}>{summaryTitle}</Typography>
          <Typography sx={{ color: "#5d6d68", fontSize: 16, lineHeight: 1.9 }}>{summaryDescription}</Typography>
        </Box>
        <Grid container spacing={3}>
          {capabilities.map((capability) => {
            const CapabilityIcon = capability.icon;
            return (
              <Grid key={capability.title} xs={12} sm={6} md={4}>
                <Paper elevation={0} sx={{ height: "100%", p: 3.5, borderRadius: 2, border: "1px solid #dce5e2", bgcolor: "#fff" }}>
                  <Box sx={{ width: 48, height: 48, display: "grid", placeItems: "center", borderRadius: 1.5, bgcolor: colors.pale, color: colors.dark, mb: 2.5 }}>
                    <CapabilityIcon />
                  </Box>
                  <Typography component="h3" sx={{ fontSize: 20, fontWeight: 700, mb: 1.5 }}>{capability.title}</Typography>
                  <Typography sx={{ color: "#63736e", lineHeight: 1.8 }}>{capability.description}</Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      <Box component="section" sx={{ bgcolor: colors.pale, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid xs={12} md={5}>
              <Typography component="h2" sx={{ fontSize: { xs: 30, md: 38 }, fontWeight: 800, lineHeight: 1.4, mb: 2 }}>{valuesTitle}</Typography>
              <Typography sx={{ color: "#586a64", fontSize: 16, lineHeight: 1.9 }}>{valuesDescription}</Typography>
            </Grid>
            <Grid xs={12} md={7}>
              <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2, bgcolor: "#fff" }}>
                <Stack spacing={3} divider={<Divider flexItem />}>
                  {values.map((value, index) => (
                    <Stack key={value.title} direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 3 }}>
                      <Typography sx={{ color: colors.dark, fontSize: 14, fontWeight: 800, pt: 0.5 }}>{String(index + 1).padStart(2, "0")}</Typography>
                      <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>{value.title}</Typography>
                        <Typography sx={{ color: "#63736e" }}>{value.description}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: colors.dark, color: "#fff", py: { xs: 7, md: 9 } }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Typography component="h2" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, mb: 2 }}>{contactTitle}</Typography>
          <Typography sx={{ color: "#d4dfdc !important", lineHeight: 1.8, mb: 4 }}>導入のご相談や機能についてのご質問は、お問い合わせフォームよりご連絡ください。</Typography>
          <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} onClick={openContact} sx={{ px: 3.5, py: 1.4, bgcolor: colors.accent, color: colors.text, fontWeight: 700, boxShadow: "none", "&:hover": { bgcolor: colors.primary, boxShadow: "none" } }}>
            お問い合わせ
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default ProductDetailPage;
